/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Verdi Dashboard API
 * API behind the Verdi irrigation dashboard: organizations, their devices, zones and irrigation schedules, and the sensor data those devices report.
 *
 * ## Authentication
 *
 * Most operations need a Descope session token, sent as `Authorization: Bearer <token>`. Each operation's `security` lists what it accepts. Role and permission names there, such as `Verdi Administrator`, are Descope roles or permissions the session must carry, and the operation's description repeats them under **Access**. Operations with `security: []` are public.
 *
 * ## Organization context
 *
 * Data belongs to organizations. A user's session acts as their primary organization, or as another organization they belong to when its ID is sent in the `User` header. Administrators must send the `User` header on organization-scoped operations, or the request is rejected with a 400.
 *
 * ## Errors
 *
 * Error bodies are JSON `{ "message": string }`.
 *
 * - **400**: the path, query or body failed validation, or the request broke a domain rule.
 * - **401 / 403**: sent by the auth middleware; the body may be empty.
 * - **409**: see below.
 * - **500**: an unexpected server error.
 *
 * ## Versioned resources
 *
 * Versioned resources carry a `versionId`. Send the `versionId` you last read on a PATCH (in the body) or a DELETE (as a query parameter). If the resource changed since, the request gets a 409: reload it and retry. A 409 that includes a `code` is a conflict specific to that operation, described on the operation. On a create, 409 means the resource already exists.
 *
 * ## Reads with a request body
 *
 * Some reads take their input as a JSON body on the HTTP `QUERY` method; these operations are marked `x-read-only: true`. Deprecated POST and GET variants serve clients that cannot send `QUERY`.
 *
 * ## Identifiers
 *
 * MongoDB ObjectIds are 24-character hexadecimal strings.
 * OpenAPI spec version: 1.0.0
 */

import type {
	DataTag,
	DefinedInitialDataOptions,
	DefinedUseQueryResult,
	MutationFunction,
	QueryClient,
	QueryFunction,
	QueryKey,
	UndefinedInitialDataOptions,
	UseMutationOptions,
	UseMutationResult,
	UseQueryOptions,
	UseQueryResult,
} from "@tanstack/react-query";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { customFetch } from "../custom-fetch.ts";
import type {
	CommandGroup,
	CreateIrrigationEventGroup201,
	CreateIrrigationEventGroup400,
	CreateIrrigationEventGroup500,
	CreateIrrigationEventGroupBody,
	DeleteIrrigationEventGroup400,
	DeleteIrrigationEventGroup404,
	DeleteIrrigationEventGroup500,
	GetIrrigationEventGroup400,
	GetIrrigationEventGroup404,
	GetIrrigationEventGroup500,
	ListIrrigationEventGroups200,
	ListIrrigationEventGroups400,
	ListIrrigationEventGroups500,
	ListIrrigationEventGroupsParams,
} from "./schemas";

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(
	query: T,
	queryKey: K,
): T & { queryKey: K } => {
	const result = { queryKey } as T & { queryKey: K };
	for (const key of Object.keys(query)) {
		// The explicit queryKey always wins, matching the previous
		// `{ ...query, queryKey }` spread where it was set last.
		if (key === "queryKey") continue;
		Object.defineProperty(result, key, {
			enumerable: true,
			configurable: true,
			get: () => (query as Record<string, unknown>)[key],
		});
	}
	return result;
};

export const getCreateIrrigationEventGroupUrl = () => {
	return `/irrigation-event-groups`;
};

/**
 * **Access:** Requires a user session token.
 * @summary Create a group of irrigation events that share a CommandGroup reference
 */
export const createIrrigationEventGroup = async (
	createIrrigationEventGroupBody: CreateIrrigationEventGroupBody,
	options?: Parameters<typeof customFetch>[1],
): Promise<CreateIrrigationEventGroup201> => {
	const getHeaders = (
		h?: NonNullable<RequestInit["headers"]>,
	): Record<string, string | readonly string[]> => {
		if (!h) return {};
		if (h instanceof Headers) return Object.fromEntries(h.entries());
		if (Symbol.iterator in h) {
			return Object.fromEntries(
				Array.from(
					h as Iterable<Iterable<string>>,
					(entry) => Array.from(entry) as [string, string],
				),
			);
		}
		const headers: Record<string, string | readonly string[]> = {};
		for (const [name, value] of Object.entries<
			string | readonly string[] | undefined
		>(h)) {
			if (value !== undefined) headers[name] = value;
		}
		return headers;
	};
	return customFetch<CreateIrrigationEventGroup201>(
		getCreateIrrigationEventGroupUrl(),
		{
			...options,
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				...getHeaders(options?.headers),
			},
			body: JSON.stringify(createIrrigationEventGroupBody),
		},
	);
};

export const getCreateIrrigationEventGroupMutationKey = () =>
	["createIrrigationEventGroup"] as const;

export const getCreateIrrigationEventGroupMutationOptions = <
	TError = CreateIrrigationEventGroup400 | void | CreateIrrigationEventGroup500,
	TContext = unknown,
>(options?: {
	mutation?: UseMutationOptions<
		Awaited<ReturnType<typeof createIrrigationEventGroup>>,
		TError,
		CreateIrrigationEventGroupMutationVariables,
		TContext
	>;
	request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
	Awaited<ReturnType<typeof createIrrigationEventGroup>>,
	TError,
	CreateIrrigationEventGroupMutationVariables,
	TContext
> => {
	const mutationKey = getCreateIrrigationEventGroupMutationKey();
	const { mutation: mutationOptions, request: requestOptions } = options
		? options.mutation &&
			"mutationKey" in options.mutation &&
			options.mutation.mutationKey
			? options
			: { ...options, mutation: { ...options.mutation, mutationKey } }
		: { mutation: { mutationKey }, request: undefined };

	const mutationFn: MutationFunction<
		Awaited<ReturnType<typeof createIrrigationEventGroup>>,
		CreateIrrigationEventGroupMutationVariables
	> = (props) => {
		const { data } = props ?? {};

		return createIrrigationEventGroup(data, requestOptions);
	};

	return { mutationFn, ...mutationOptions };
};

export type CreateIrrigationEventGroupMutationResult = NonNullable<
	Awaited<ReturnType<typeof createIrrigationEventGroup>>
>;
export type CreateIrrigationEventGroupMutationBody =
	CreateIrrigationEventGroupBody;
export type CreateIrrigationEventGroupMutationError =
	| CreateIrrigationEventGroup400
	| void
	| CreateIrrigationEventGroup500;
export type CreateIrrigationEventGroupMutationVariables = {
	data: CreateIrrigationEventGroupBody;
};

/**
 * @summary Create a group of irrigation events that share a CommandGroup reference
 */
export const useCreateIrrigationEventGroup = <
	TError = CreateIrrigationEventGroup400 | void | CreateIrrigationEventGroup500,
	TContext = unknown,
>(
	options?: {
		mutation?: UseMutationOptions<
			Awaited<ReturnType<typeof createIrrigationEventGroup>>,
			TError,
			CreateIrrigationEventGroupMutationVariables,
			TContext
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseMutationResult<
	Awaited<ReturnType<typeof createIrrigationEventGroup>>,
	TError,
	CreateIrrigationEventGroupMutationVariables,
	TContext
> => {
	return useMutation(
		getCreateIrrigationEventGroupMutationOptions(options),
		queryClient,
	);
};
export const getListIrrigationEventGroupsUrl = (
	params?: ListIrrigationEventGroupsParams,
) => {
	const normalizedParams = new URLSearchParams();

	Object.entries(params || {}).forEach(([key, value]) => {
		if (value !== undefined) {
			normalizedParams.append(key, value === null ? "null" : String(value));
		}
	});

	const stringifiedParams = normalizedParams.toString();

	return stringifiedParams.length > 0
		? `/irrigation-event-groups?${stringifiedParams}`
		: `/irrigation-event-groups`;
};

/**
 * **Access:** Requires a user session token.
 * @summary List irrigation event groups for the authenticated user, optionally filtered by name or source
 */
export const listIrrigationEventGroups = async (
	params?: ListIrrigationEventGroupsParams,
	options?: Parameters<typeof customFetch>[1],
): Promise<ListIrrigationEventGroups200> => {
	return customFetch<ListIrrigationEventGroups200>(
		getListIrrigationEventGroupsUrl(params),
		{
			...options,
			method: "GET",
		},
	);
};

export const getListIrrigationEventGroupsQueryKey = (
	params?: ListIrrigationEventGroupsParams,
) => {
	return ["irrigation-event-groups", ...(params ? [params] : [])] as const;
};

export const getListIrrigationEventGroupsQueryOptions = <
	TData = Awaited<ReturnType<typeof listIrrigationEventGroups>>,
	TError = ListIrrigationEventGroups400 | void | ListIrrigationEventGroups500,
>(
	params?: ListIrrigationEventGroupsParams,
	options?: {
		query?: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof listIrrigationEventGroups>>,
				TError,
				TData
			>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
) => {
	const { query: queryOptions, request: requestOptions } = options ?? {};

	const queryKey =
		queryOptions?.queryKey ?? getListIrrigationEventGroupsQueryKey(params);

	const queryFn: QueryFunction<
		Awaited<ReturnType<typeof listIrrigationEventGroups>>
	> = ({ signal }) =>
		listIrrigationEventGroups(params, { signal, ...requestOptions });

	return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
		Awaited<ReturnType<typeof listIrrigationEventGroups>>,
		TError,
		TData
	> & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListIrrigationEventGroupsQueryResult = NonNullable<
	Awaited<ReturnType<typeof listIrrigationEventGroups>>
>;
export type ListIrrigationEventGroupsQueryError =
	| ListIrrigationEventGroups400
	| void
	| ListIrrigationEventGroups500;

export function useListIrrigationEventGroups<
	TData = Awaited<ReturnType<typeof listIrrigationEventGroups>>,
	TError = ListIrrigationEventGroups400 | void | ListIrrigationEventGroups500,
>(
	params: undefined | ListIrrigationEventGroupsParams,
	options: {
		query: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof listIrrigationEventGroups>>,
				TError,
				TData
			>
		> &
			Pick<
				DefinedInitialDataOptions<
					Awaited<ReturnType<typeof listIrrigationEventGroups>>,
					TError,
					Awaited<ReturnType<typeof listIrrigationEventGroups>>
				>,
				"initialData"
			>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListIrrigationEventGroups<
	TData = Awaited<ReturnType<typeof listIrrigationEventGroups>>,
	TError = ListIrrigationEventGroups400 | void | ListIrrigationEventGroups500,
>(
	params?: ListIrrigationEventGroupsParams,
	options?: {
		query?: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof listIrrigationEventGroups>>,
				TError,
				TData
			>
		> &
			Pick<
				UndefinedInitialDataOptions<
					Awaited<ReturnType<typeof listIrrigationEventGroups>>,
					TError,
					Awaited<ReturnType<typeof listIrrigationEventGroups>>
				>,
				"initialData"
			>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListIrrigationEventGroups<
	TData = Awaited<ReturnType<typeof listIrrigationEventGroups>>,
	TError = ListIrrigationEventGroups400 | void | ListIrrigationEventGroups500,
>(
	params?: ListIrrigationEventGroupsParams,
	options?: {
		query?: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof listIrrigationEventGroups>>,
				TError,
				TData
			>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary List irrigation event groups for the authenticated user, optionally filtered by name or source
 */

export function useListIrrigationEventGroups<
	TData = Awaited<ReturnType<typeof listIrrigationEventGroups>>,
	TError = ListIrrigationEventGroups400 | void | ListIrrigationEventGroups500,
>(
	params?: ListIrrigationEventGroupsParams,
	options?: {
		query?: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof listIrrigationEventGroups>>,
				TError,
				TData
			>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
} {
	const queryOptions = getListIrrigationEventGroupsQueryOptions(
		params,
		options,
	);

	const query = useQuery(queryOptions, queryClient) as UseQueryResult<
		TData,
		TError
	> & { queryKey: DataTag<QueryKey, TData, TError> };

	return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List irrigation event groups for the authenticated user, optionally filtered by name or source
 */
export const useSetListIrrigationEventGroupsQueryData = () => {
	const queryClient = useQueryClient();
	return (
		params: ListIrrigationEventGroupsParams | undefined,
		updater:
			| Awaited<ReturnType<typeof listIrrigationEventGroups>>
			| undefined
			| ((
					old:
						| Awaited<ReturnType<typeof listIrrigationEventGroups>>
						| undefined,
			  ) => Awaited<ReturnType<typeof listIrrigationEventGroups>> | undefined),
		$exactMatch: boolean = true,
	) => {
		queryClient.setQueriesData<
			Awaited<ReturnType<typeof listIrrigationEventGroups>>
		>(
			{
				exact: $exactMatch,
				queryKey: getListIrrigationEventGroupsQueryKey(params),
			},
			updater,
		);
	};
};

/**
 * @summary List irrigation event groups for the authenticated user, optionally filtered by name or source
 */
export const useGetListIrrigationEventGroupsQueryData = () => {
	const queryClient = useQueryClient();
	return (params?: ListIrrigationEventGroupsParams) =>
		queryClient.getQueryData<
			Awaited<ReturnType<typeof listIrrigationEventGroups>>
		>(getListIrrigationEventGroupsQueryKey(params));
};

export const getGetIrrigationEventGroupUrl = (
	irrigationEventGroupId: string,
) => {
	return `/irrigation-event-groups/${irrigationEventGroupId}`;
};

/**
 * **Access:** Requires a user session token.
 * @summary Get a single irrigation event group by ID
 */
export const getIrrigationEventGroup = async (
	irrigationEventGroupId: string,
	options?: Parameters<typeof customFetch>[1],
): Promise<CommandGroup> => {
	return customFetch<CommandGroup>(
		getGetIrrigationEventGroupUrl(irrigationEventGroupId),
		{
			...options,
			method: "GET",
		},
	);
};

export const getGetIrrigationEventGroupQueryKey = (
	irrigationEventGroupId: string,
) => {
	return ["irrigation-event-groups", irrigationEventGroupId] as const;
};

export const getGetIrrigationEventGroupQueryOptions = <
	TData = Awaited<ReturnType<typeof getIrrigationEventGroup>>,
	TError =
		| GetIrrigationEventGroup400
		| void
		| GetIrrigationEventGroup404
		| GetIrrigationEventGroup500,
>(
	irrigationEventGroupId: string,
	options?: {
		query?: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof getIrrigationEventGroup>>,
				TError,
				TData
			>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
) => {
	const { query: queryOptions, request: requestOptions } = options ?? {};

	const queryKey =
		queryOptions?.queryKey ??
		getGetIrrigationEventGroupQueryKey(irrigationEventGroupId);

	const queryFn: QueryFunction<
		Awaited<ReturnType<typeof getIrrigationEventGroup>>
	> = ({ signal }) =>
		getIrrigationEventGroup(irrigationEventGroupId, {
			signal,
			...requestOptions,
		});

	return {
		queryKey,
		queryFn,
		enabled:
			irrigationEventGroupId !== null && irrigationEventGroupId !== undefined,
		...queryOptions,
	} as UseQueryOptions<
		Awaited<ReturnType<typeof getIrrigationEventGroup>>,
		TError,
		TData
	> & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetIrrigationEventGroupQueryResult = NonNullable<
	Awaited<ReturnType<typeof getIrrigationEventGroup>>
>;
export type GetIrrigationEventGroupQueryError =
	| GetIrrigationEventGroup400
	| void
	| GetIrrigationEventGroup404
	| GetIrrigationEventGroup500;

export function useGetIrrigationEventGroup<
	TData = Awaited<ReturnType<typeof getIrrigationEventGroup>>,
	TError =
		| GetIrrigationEventGroup400
		| void
		| GetIrrigationEventGroup404
		| GetIrrigationEventGroup500,
>(
	irrigationEventGroupId: string,
	options: {
		query: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof getIrrigationEventGroup>>,
				TError,
				TData
			>
		> &
			Pick<
				DefinedInitialDataOptions<
					Awaited<ReturnType<typeof getIrrigationEventGroup>>,
					TError,
					Awaited<ReturnType<typeof getIrrigationEventGroup>>
				>,
				"initialData"
			>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetIrrigationEventGroup<
	TData = Awaited<ReturnType<typeof getIrrigationEventGroup>>,
	TError =
		| GetIrrigationEventGroup400
		| void
		| GetIrrigationEventGroup404
		| GetIrrigationEventGroup500,
>(
	irrigationEventGroupId: string,
	options?: {
		query?: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof getIrrigationEventGroup>>,
				TError,
				TData
			>
		> &
			Pick<
				UndefinedInitialDataOptions<
					Awaited<ReturnType<typeof getIrrigationEventGroup>>,
					TError,
					Awaited<ReturnType<typeof getIrrigationEventGroup>>
				>,
				"initialData"
			>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetIrrigationEventGroup<
	TData = Awaited<ReturnType<typeof getIrrigationEventGroup>>,
	TError =
		| GetIrrigationEventGroup400
		| void
		| GetIrrigationEventGroup404
		| GetIrrigationEventGroup500,
>(
	irrigationEventGroupId: string,
	options?: {
		query?: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof getIrrigationEventGroup>>,
				TError,
				TData
			>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get a single irrigation event group by ID
 */

export function useGetIrrigationEventGroup<
	TData = Awaited<ReturnType<typeof getIrrigationEventGroup>>,
	TError =
		| GetIrrigationEventGroup400
		| void
		| GetIrrigationEventGroup404
		| GetIrrigationEventGroup500,
>(
	irrigationEventGroupId: string,
	options?: {
		query?: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof getIrrigationEventGroup>>,
				TError,
				TData
			>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
} {
	const queryOptions = getGetIrrigationEventGroupQueryOptions(
		irrigationEventGroupId,
		options,
	);

	const query = useQuery(queryOptions, queryClient) as UseQueryResult<
		TData,
		TError
	> & { queryKey: DataTag<QueryKey, TData, TError> };

	return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get a single irrigation event group by ID
 */
export const useSetGetIrrigationEventGroupQueryData = () => {
	const queryClient = useQueryClient();
	return (
		irrigationEventGroupId: string,
		updater:
			| Awaited<ReturnType<typeof getIrrigationEventGroup>>
			| undefined
			| ((
					old: Awaited<ReturnType<typeof getIrrigationEventGroup>> | undefined,
			  ) => Awaited<ReturnType<typeof getIrrigationEventGroup>> | undefined),
		$exactMatch: boolean = true,
	) => {
		queryClient.setQueriesData<
			Awaited<ReturnType<typeof getIrrigationEventGroup>>
		>(
			{
				exact: $exactMatch,
				queryKey: getGetIrrigationEventGroupQueryKey(irrigationEventGroupId),
			},
			updater,
		);
	};
};

/**
 * @summary Get a single irrigation event group by ID
 */
export const useGetGetIrrigationEventGroupQueryData = () => {
	const queryClient = useQueryClient();
	return (irrigationEventGroupId: string) =>
		queryClient.getQueryData<
			Awaited<ReturnType<typeof getIrrigationEventGroup>>
		>(getGetIrrigationEventGroupQueryKey(irrigationEventGroupId));
};

export const getDeleteIrrigationEventGroupUrl = (
	irrigationEventGroupId: string,
) => {
	return `/irrigation-event-groups/${irrigationEventGroupId}`;
};

/**
 * **Access:** Requires a user session token.
 * @summary Delete a command group and all its associated irrigation events
 */
export const deleteIrrigationEventGroup = async (
	irrigationEventGroupId: string,
	options?: Parameters<typeof customFetch>[1],
): Promise<CommandGroup> => {
	return customFetch<CommandGroup>(
		getDeleteIrrigationEventGroupUrl(irrigationEventGroupId),
		{
			...options,
			method: "DELETE",
		},
	);
};

export const getDeleteIrrigationEventGroupMutationKey = () =>
	["deleteIrrigationEventGroup"] as const;

export const getDeleteIrrigationEventGroupMutationOptions = <
	TError =
		| DeleteIrrigationEventGroup400
		| void
		| DeleteIrrigationEventGroup404
		| DeleteIrrigationEventGroup500,
	TContext = unknown,
>(options?: {
	mutation?: UseMutationOptions<
		Awaited<ReturnType<typeof deleteIrrigationEventGroup>>,
		TError,
		DeleteIrrigationEventGroupMutationVariables,
		TContext
	>;
	request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
	Awaited<ReturnType<typeof deleteIrrigationEventGroup>>,
	TError,
	DeleteIrrigationEventGroupMutationVariables,
	TContext
> => {
	const mutationKey = getDeleteIrrigationEventGroupMutationKey();
	const { mutation: mutationOptions, request: requestOptions } = options
		? options.mutation &&
			"mutationKey" in options.mutation &&
			options.mutation.mutationKey
			? options
			: { ...options, mutation: { ...options.mutation, mutationKey } }
		: { mutation: { mutationKey }, request: undefined };

	const mutationFn: MutationFunction<
		Awaited<ReturnType<typeof deleteIrrigationEventGroup>>,
		DeleteIrrigationEventGroupMutationVariables
	> = (props) => {
		const { irrigationEventGroupId } = props ?? {};

		return deleteIrrigationEventGroup(irrigationEventGroupId, requestOptions);
	};

	return { mutationFn, ...mutationOptions };
};

export type DeleteIrrigationEventGroupMutationResult = NonNullable<
	Awaited<ReturnType<typeof deleteIrrigationEventGroup>>
>;

export type DeleteIrrigationEventGroupMutationError =
	| DeleteIrrigationEventGroup400
	| void
	| DeleteIrrigationEventGroup404
	| DeleteIrrigationEventGroup500;
export type DeleteIrrigationEventGroupMutationVariables = {
	irrigationEventGroupId: string;
};

/**
 * @summary Delete a command group and all its associated irrigation events
 */
export const useDeleteIrrigationEventGroup = <
	TError =
		| DeleteIrrigationEventGroup400
		| void
		| DeleteIrrigationEventGroup404
		| DeleteIrrigationEventGroup500,
	TContext = unknown,
>(
	options?: {
		mutation?: UseMutationOptions<
			Awaited<ReturnType<typeof deleteIrrigationEventGroup>>,
			TError,
			DeleteIrrigationEventGroupMutationVariables,
			TContext
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseMutationResult<
	Awaited<ReturnType<typeof deleteIrrigationEventGroup>>,
	TError,
	DeleteIrrigationEventGroupMutationVariables,
	TContext
> => {
	return useMutation(
		getDeleteIrrigationEventGroupMutationOptions(options),
		queryClient,
	);
};
